import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Factory, Award, Truck, Shield, Phone, Zap, Package, Users, Settings, Palette, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';

const OemChargerManufacturerNoida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const oemServices = [
    { icon: Palette, title: 'Custom Branding', desc: 'Logo printing, embossing, custom colors on chargers and packaging' },
    { icon: Box, title: 'Private Label Packaging', desc: 'Fully customized retail packaging with your brand identity' },
    { icon: Settings, title: 'Custom Specifications', desc: 'Tailored wattage, ports, protocols as per your requirements' },
    { icon: Factory, title: 'Exclusive Designs', desc: 'Unique charger designs exclusive to your brand' }
  ];

  const chargerOEMOptions = [
    { wattage: '15W-20W', type: 'Entry Level', moq: '10,000 units', lead: '15 days' },
    { wattage: '25W-35W', type: 'Mid Range', moq: '7,500 units', lead: '18 days' },
    { wattage: '45W-65W', type: 'Premium', moq: '5,000 units', lead: '20 days' },
    { wattage: 'Custom', type: 'Specialized', moq: 'Negotiable', lead: '25-30 days' }
  ];

  const faqs = [
    {
      q: 'Who is the best OEM charger manufacturer in Noida?',
      a: 'RSD Accessories Pvt Ltd in Sector-57, Noida is a leading OEM charger manufacturer. With ISO 9001:2015 certification, BIS-compliant products, and 8+ years of experience, RSD has partnered with 100+ brands for private label charger manufacturing. We offer complete customization from 15W to 65W chargers.'
    },
    {
      q: 'What OEM charger services does RSD provide?',
      a: 'RSD provides comprehensive OEM charger services: custom branding (logo printing/embossing), custom colors (including matte metallic finishes), private label packaging design, custom wattage specifications, multi-protocol support (QC/PD/VOOC), BIS certification support, and exclusive product designs.'
    },
    {
      q: 'What is the minimum order quantity for OEM chargers?',
      a: 'Our OEM charger MOQ varies by wattage: 15W-20W chargers: 10,000 units, 25W-35W chargers: 7,500 units, 45W-65W premium chargers: 5,000 units. For new partners and custom specifications, MOQ can be negotiated based on the project scope.'
    },
    {
      q: 'Can RSD help with BIS certification for chargers?',
      a: 'Yes, RSD provides BIS certification support for OEM partners. Our chargers are manufactured in a BIS-certified facility, and we can guide you through the certification process for your private label brand. All safety and compliance documentation is provided.'
    },
    {
      q: 'What is the lead time for OEM charger orders?',
      a: 'Standard lead time for OEM chargers: 15-20 days for ready designs with branding, 25-30 days for custom specifications or new designs. First-time orders include sample approval process (5-7 days). Repeat orders can be fulfilled faster.'
    },
    {
      q: 'Does RSD provide charger design and development?',
      a: 'Yes, our ODM services include charger design and development. We can create exclusive charger designs based on your specifications, handle tooling and mold creation, develop prototypes for approval, and manufacture the final product. This is ideal for brands wanting unique products.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>OEM Charger Manufacturer in Noida | RSD Accessories - Private Label Manufacturing</title>
        <meta name="description" content="RSD Accessories - Leading OEM charger manufacturer in Noida. Custom branding, private label, BIS certified. 15W-65W chargers. 100+ brand partnerships. Wholesale OEM pricing." />
        <meta name="keywords" content="oem charger manufacturer noida, private label charger, custom charger manufacturer, white label charger india, charger oem india, bis certified oem charger, bulk charger manufacturer" />
        <link rel="canonical" href="https://rsdaccessories.com/oem-charger-manufacturer-noida" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-black py-20 md:py-28">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FF4D00" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <span className="inline-block bg-[#FF4D00] text-white px-4 py-1 rounded-full text-sm font-semibold mb-6">
                OEM & Private Label Solutions
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                OEM Charger Manufacturer in <span className="text-[#FF4D00]">Noida</span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto mb-8">
                RSD Accessories - Your Partner for Private Label Charger Manufacturing | BIS Certified | 100+ Brand Partnerships
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#FF4D00] hover:bg-[#E64500] text-white px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Discuss OEM Project
                  </Button>
                </Link>
                <Link to="/shop/mobile-chargers">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                    View Charger Range
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Stats */}
        <section className="py-8 bg-neutral-100 border-y border-neutral-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">100+</span>
                <span className="text-sm text-neutral-600">OEM Brand Partners</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">BIS</span>
                <span className="text-sm text-neutral-600">Certified Facility</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">15-65W</span>
                <span className="text-sm text-neutral-600">Wattage Range</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl font-black text-[#FF4D00]">15-20</span>
                <span className="text-sm text-neutral-600">Days Lead Time</span>
              </div>
            </div>
          </div>
        </section>

        {/* OEM Services */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                OEM Charger Manufacturing Services
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                Complete private label solutions - from concept to delivery
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {oemServices.map((service, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border border-neutral-100 text-center">
                  <div className="w-16 h-16 bg-[#FF4D00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-8 h-8 text-[#FF4D00]" />
                  </div>
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">{service.title}</h3>
                  <p className="text-neutral-600">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OEM Options Table */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                OEM Charger Options & MOQ
              </h2>
            </div>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-neutral-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Wattage</th>
                    <th className="px-6 py-4 text-left">Type</th>
                    <th className="px-6 py-4 text-left">MOQ</th>
                    <th className="px-6 py-4 text-left">Lead Time</th>
                  </tr>
                </thead>
                <tbody>
                  {chargerOEMOptions.map((option, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}>
                      <td className="px-6 py-4 font-semibold text-[#FF4D00]">{option.wattage}</td>
                      <td className="px-6 py-4">{option.type}</td>
                      <td className="px-6 py-4">{option.moq}</td>
                      <td className="px-6 py-4">{option.lead}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-neutral-500 mt-4 text-sm">
              * MOQ and lead times are indicative. Contact us for exact quotes based on your requirements.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                OEM Manufacturing Process
              </h2>
            </div>
            <div className="grid md:grid-cols-5 gap-4">
              {[
                { step: '01', title: 'Consultation', desc: 'Discuss requirements, wattage, protocols' },
                { step: '02', title: 'Design', desc: 'Branding, packaging design approval' },
                { step: '03', title: 'Sampling', desc: 'Prototype production and approval' },
                { step: '04', title: 'Production', desc: 'Bulk manufacturing with QC' },
                { step: '05', title: 'Delivery', desc: 'Packaging and Pan India shipping' }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-[#FF4D00] text-white rounded-full flex items-center justify-center text-2xl font-black mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-neutral-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-neutral-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-neutral-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                OEM Charger FAQs
              </h2>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-neutral-800 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-white mb-3">{faq.q}</h3>
                  <p className="text-neutral-300">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Launch Your Charger Brand with RSD
            </h2>
            <p className="text-xl text-white/90 mb-8">
              From design to delivery - complete OEM manufacturing solutions
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <a href="mailto:contact@rsdaccessories.com">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg">
                  Email: contact@rsdaccessories.com
                </Button>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default OemChargerManufacturerNoida;
